import { cache } from "react";
import { processMemo } from "@/lib/processMemo";

// Public side of the PromptAI catalogue (the app's prompts, served by the
// PHP API on the PromptAI box behind CloudFront's /promptai/* behaviour).
// The admin editor lives in promptaiAdmin.ts.
export const PROMPTAI_API = "https://www.nohaya.com/promptai/api";
export const PROMPTAI_PLAY_URL = "https://play.google.com/store/apps/details?id=com.crede.prompt_generator";

export type PromptCategory = {
  id: number;
  name: string;
  slug: string;
  description: string;
  colorIndex: number;
  count: number;
};

export type PromptSummary = {
  id: number;
  slug: string;
  title: string;
  description: string;
  hint: string;
  imageUrl: string | null;
  platforms: string[];
  isPro: boolean;
  views: number;
  favorites: number;
  createdAt: string;
  categoryId: number;
  categoryName: string;
  categorySlug: string;
  colorIndex: number;
};

/** The slim shape the gallery's client-side filter/search gets. imageUrl is the small card thumbnail. */
export type PromptCard = Pick<
  PromptSummary,
  "id" | "slug" | "title" | "description" | "imageUrl" | "isPro" | "views" | "createdAt" | "categoryName" | "categorySlug" | "colorIndex"
>;

/** The card-size WebP of a prompt's example image (src/app/prompt-thumbs). Changes whenever the image does. */
export function promptThumbPath(id: number, imageUrl: string): string {
  // Two 32-bit FNV-1a hashes (different seeds) -> 10 hex chars. Pure JS:
  // client components import this module too.
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < imageUrl.length; i++) {
    const c = imageUrl.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x811c9dc5) >>> 0;
  }
  const hash = (a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0")).slice(0, 10);
  return `/prompt-thumbs/${id}-${hash}.webp`;
}

export const PLATFORM_INFO: Record<string, { label: string; url: string }> = {
  chatgpt: { label: "ChatGPT", url: "https://chatgpt.com/" },
  gemini: { label: "Gemini", url: "https://gemini.google.com/app" },
  midjourney: { label: "Midjourney", url: "https://www.midjourney.com/imagine" },
  leonardo: { label: "Leonardo AI", url: "https://app.leonardo.ai/" },
  runway: { label: "Runway", url: "https://app.runwayml.com/" },
  klingai: { label: "Kling AI", url: "https://klingai.com/" },
};

/** Gradient pairs for covers of prompts without an example image, by category color_index (as in the app). */
export const COVER_GRADIENTS: [string, string][] = [
  ["#0ea5e9", "#22d3ee"],
  ["#7c3aed", "#c084fc"],
  ["#059669", "#34d399"],
  ["#db2777", "#f472b6"],
  ["#d97706", "#fbbf24"],
  ["#ea580c", "#fb7185"],
  ["#2563eb", "#818cf8"],
  ["#0f766e", "#5eead4"],
];

export function coverGradient(colorIndex: number): [string, string] {
  return COVER_GRADIENTS[((colorIndex % COVER_GRADIENTS.length) + COVER_GRADIENTS.length) % COVER_GRADIENTS.length];
}

export function slugifyTitle(title: string): string {
  return (
    // ASCII-only before lowercasing, so PHP's byte-wise strtolower() agrees.
    title
      .replace(/[^A-Za-z0-9]+/g, "-")
      .toLowerCase()
      .replace(/^-+|-+$/g, "")
      .slice(0, 90)
      .replace(/-+$/g, "") || "prompt"
  );
}

/**
 * id -> URL slug for every active prompt: the title alone, e.g.
 * /prompts/navratri-2026-couple-ai-photo-editing-prompt-chatgpt. Two prompts
 * with the same title: the older (lower id) keeps the bare slug, later ones
 * get -2, -3… (skipping any slug another title produces naturally).
 *
 * Must stay identical to promptai_slug_map() in the PromptAI backend
 * (backend/includes/prompt_slugs.php), which the app resolves shared links
 * with, and which returns `slug` in its API responses.
 */
export function buildPromptSlugMap(prompts: { id: number; title: string }[]): Map<number, string> {
  const sorted = [...prompts].sort((a, b) => a.id - b.id);
  const natural = new Set(sorted.map((p) => slugifyTitle(p.title)));
  const taken = new Set<string>();
  const map = new Map<number, string>();
  for (const p of sorted) {
    const base = slugifyTitle(p.title);
    let slug = base;
    if (taken.has(slug)) {
      let n = 2;
      while (taken.has(`${base}-${n}`) || natural.has(`${base}-${n}`)) n++;
      slug = `${base}-${n}`;
    }
    taken.add(slug);
    map.set(p.id, slug);
  }
  return map;
}

type RawPrompt = {
  id: number;
  title: string;
  description?: string | null;
  hint?: string | null;
  content?: string;
  image_url?: string | null;
  platforms?: string[] | null;
  is_pro?: boolean | number;
  view_count?: number;
  favorite_count?: number;
  created_at?: string;
  category_id?: number;
  category_name?: string | null;
  category_color_index?: number;
  locked?: boolean;
  ad_gated?: boolean;
  teaser?: string;
  word_count?: number;
};

type RawCategory = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  color_index?: number;
  prompt_count?: number;
};

/** GET with retries; throws when failures persist so ISR keeps the last good page instead of caching an empty one. */
async function fetchPromptApi<T>(path: string, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const res = await fetch(`${PROMPTAI_API}${path}`, {
        signal: AbortSignal.timeout(10000),
        next: { revalidate: 600, tags: ["prompts"] },
      });
      if (res.ok) return (await res.json()) as T;
      lastError = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastError = err;
    }
    await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
  }
  throw new Error(`PromptAI API ${path} failed after ${attempts} attempts: ${String(lastError)}`);
}

function cleanText(value: string | null | undefined): string {
  return (value ?? "").replace(/\r\n/g, "\n").trim();
}

/** Category names as typed in the admin: one-word typos like "BirthDay"/"BIker" -> "Birthday"/"Biker". */
function cleanCategoryName(value: string | null | undefined): string {
  const name = cleanText(value);
  return /^[A-Za-z]+$/.test(name) ? name[0].toUpperCase() + name.slice(1).toLowerCase() : name;
}

type Catalogue = { prompts: PromptSummary[]; categories: PromptCategory[] };

async function loadCatalogue(): Promise<Catalogue> {
  const [catData, raw] = await Promise.all([
    // include_imageless=1: the app only sees prompts with an image, the site lists them all.
    fetchPromptApi<{ success: boolean; categories: RawCategory[] }>("/categories/list.php?include_imageless=1"),
    (async () => {
      // list.php caps limit at 50; ~850 prompts is ~17 pages.
      const all: RawPrompt[] = [];
      for (let page = 1; page <= 200; page++) {
        const data = await fetchPromptApi<{ success: boolean; prompts: RawPrompt[]; has_more: boolean }>(
          `/prompts/list.php?page=${page}&limit=50&include_imageless=1`
        );
        all.push(...data.prompts);
        if (!data.has_more) break;
      }
      return all;
    })(),
  ]);
  if (!raw.length) throw new Error("PromptAI API returned no prompts");

  const categorySlugById = new Map(catData.categories.map((c) => [c.id, c.slug]));
  const slugs = buildPromptSlugMap(raw);
  const prompts: PromptSummary[] = raw.map((p) => ({
    id: p.id,
    slug: slugs.get(p.id)!,
    title: cleanText(p.title),
    description: cleanText(p.description),
    hint: cleanText(p.hint),
    imageUrl: p.image_url || null,
    platforms: Array.isArray(p.platforms) ? p.platforms : [],
    isPro: Boolean(p.is_pro),
    views: p.view_count ?? 0,
    favorites: p.favorite_count ?? 0,
    createdAt: p.created_at ?? "",
    categoryId: p.category_id ?? 0,
    categoryName: cleanCategoryName(p.category_name) || "Prompts",
    categorySlug: categorySlugById.get(p.category_id ?? 0) ?? "",
    colorIndex: p.category_color_index ?? 0,
  }));

  const counts = new Map<number, number>();
  for (const p of prompts) counts.set(p.categoryId, (counts.get(p.categoryId) ?? 0) + 1);
  const categories: PromptCategory[] = catData.categories
    .map((c) => ({
      id: c.id,
      name: cleanCategoryName(c.name),
      slug: c.slug,
      description: cleanText(c.description),
      colorIndex: c.color_index ?? 0,
      count: counts.get(c.id) ?? 0,
    }))
    .filter((c) => c.count > 0);

  return { prompts, categories };
}

// One parsed copy per server process, refreshed every 10 minutes (see
// processMemo.ts) -- every prompt page, category page and the sitemap read it.
const catalogueMemo = processMemo(loadCatalogue, 10 * 60 * 1000);

export const getPromptCatalogue = cache((): Promise<Catalogue> => catalogueMemo.get());

export function expirePromptCatalogue(): void {
  catalogueMemo.expire();
}

/**
 * The prompt a /prompts/{slug} URL names. Older links (the app's shares and
 * the 2026 site) carry the id at the end -- "…-portrait-753" -- or are the
 * bare id; those come back with `redirect` set so the page 301s to the
 * title-only URL. An exact slug match always wins, so a title that happens
 * to end in a number ("…-navratri-2026") still resolves to itself.
 */
export async function resolvePromptSlug(slug: string): Promise<{ prompt: PromptSummary; redirect: boolean } | null> {
  const { prompts } = await getPromptCatalogue();
  const exact = prompts.find((p) => p.slug === slug);
  if (exact) return { prompt: exact, redirect: false };
  const legacyId = Number((/^(?:.*-)?(\d+)$/.exec(slug) ?? [])[1]);
  const legacy = legacyId ? prompts.find((p) => p.id === legacyId) : undefined;
  return legacy ? { prompt: legacy, redirect: true } : null;
}

export type PromptDetail = {
  /** Full text for a free prompt; "" for a Pro one (buyers unlock it client-side). */
  content: string;
  /** One sentence on what the prompt makes -- all a Pro prompt's page shows of it. */
  teaser: string;
  wordCount: number;
};

/**
 * The detail endpoint as an anonymous caller. track=0: the site's own
 * (re)renders aren't views; paid_only=1: never accept ad-gated text, which
 * is only for the app (see the backend's prompt_guard.php).
 */
export const getPromptDetail = cache(async (id: number): Promise<PromptDetail> => {
  const data = await fetchPromptApi<RawPrompt>(`/prompts/detail.php?id=${id}&track=0&paid_only=1`);
  return {
    content: data.locked || data.ad_gated ? "" : cleanText(data.content),
    teaser: cleanText(data.teaser),
    wordCount: data.word_count ?? 0,
  };
});

export function toPromptCard(p: PromptSummary, descriptionLength = 140): PromptCard {
  const description = p.description.length > descriptionLength ? `${p.description.slice(0, descriptionLength - 1).trimEnd()}…` : p.description;
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    description,
    imageUrl: p.imageUrl ? promptThumbPath(p.id, p.imageUrl) : null,
    isPro: p.isPro,
    views: p.views,
    createdAt: p.createdAt,
    categoryName: p.categoryName,
    categorySlug: p.categorySlug,
    colorIndex: p.colorIndex,
  };
}

/** "📸 Upload one clear…\n😊 Keep…" -> one step per line, emoji kept. */
export function hintSteps(hint: string): string[] {
  return hint
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Sitemap/lastmod-style date from the API's "2026-10-10 17:20:44" (server time, treated as UTC). */
export function promptDate(createdAt: string): Date | null {
  const d = new Date(createdAt.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime()) ? null : d;
}
